﻿import { Vector2 } from "../../common/vector2.js";
import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";

const DESIGN_W = 950;
const DESIGN_H = 1460;

const PANEL_X = 0;
const PANEL_Y = 149;
const PANEL_W = 909;
const PANEL_H = 1400;

const HIT_X = 0;
const HIT_Y = 105;
const HIT_W = 915;
const HIT_H = 1330;

const BANNER_W = 717;
const BANNER_H = 255;
const BANNER_Y = 130;

const SHADOW_SIZE = 601;
const SHADOW_Y = 310;

const BOX_W = 512;
const BOX_H = 460;
const BOX_Y = 360;

const BLOCK_W = 800;
const BLOCK_H = 280;
const BLOCK_Y = 860;
const BLOCK_RADIUS = 24;
const BLOCK_TEXT_SIZE = 55;
const BLOCK_LINE_HEIGHT = 1.2;

const BUTTON_W = 700;
const BUTTON_H = 280;
const BUTTON_Y = 1200;

const CLOSE_X = 810;
const CLOSE_Y = 70;
const CLOSE_SIZE = 138;

const CONTENT_TOP = CLOSE_Y;
const CONTENT_BOTTOM = PANEL_Y + PANEL_H;

export class LevelBoxDialog extends AbstractDialog
{
  private close_btn!: UIImage;

  constructor( private readonly next_box_level: number = 20 )
  {
    const body_w = LayoutScaler.widthFromDesign( DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( DESIGN_H );
    const content_h = LayoutScaler.heightFromDesign( CONTENT_BOTTOM - CONTENT_TOP );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - content_h ) / 2
      - LayoutScaler.heightFromDesign( CONTENT_TOP );

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createTopBanner();
    this.createBoxImage();
    this.createInfoBlock();
    this.createButton();
    this.createCloseButton();
  }

  private panelX( design_x: number ): number
  {
    return LayoutScaler.widthFromDesign( PANEL_X + design_x );
  }

  private panelCenteredX( design_w: number ): number
  {
    return this.panelX( ( PANEL_W - design_w ) / 2 );
  }

  private panelY( design_y: number ): number
  {
    return LayoutScaler.heightFromDesign( design_y );
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      this.panelX( 0 ),
      this.panelY( PANEL_Y ),
      LayoutScaler.widthFromDesign( PANEL_W ),
      LayoutScaler.heightFromDesign( PANEL_H ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );

    this.addChild( bg );
  }

  private createTopBanner(): void
  {
    const banner_w = LayoutScaler.widthFromDesign( BANNER_W );
    const banner_h = LayoutScaler.heightFromDesign( BANNER_H );

    const banner = new UIImage(
      this.ctx,
      this.panelCenteredX( BANNER_W ),
      this.panelY( BANNER_Y ),
      banner_w,
      banner_h,
      ImgID.UI_BoosterNameBg
    );

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 45 ),
      LayoutScaler.yFromDesign( 6 ),
      banner_w - LayoutScaler.widthFromDesign( 90 ),
      banner_h - LayoutScaler.heightFromDesign( 24 ),
      {
        localization_key: "LEVEL_BOX.TITLE_MULTILINE",
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: ColorScheme.booster_dialog_quantity_outline,
        auto_wrap: true,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
        line_height: 0.95,
      }
    );

    banner.addChild( title );
    this.addChild( banner );
  }

  private createBoxImage(): void
  {
    // const box_shadow = new UIImage(
    //   this.ctx,
    //   this.panelCenteredX( SHADOW_SIZE ),
    //   this.panelY( SHADOW_Y ),
    //   LayoutScaler.widthFromDesign( SHADOW_SIZE ),
    //   LayoutScaler.heightFromDesign( SHADOW_SIZE ),
    //   ImgID.UI_LevelBoxShadow,
    // );

    // this.addChild( box_shadow );

    const box = new UIImage(
      this.ctx,
      this.panelCenteredX( BOX_W ),
      this.panelY( BOX_Y ),
      LayoutScaler.widthFromDesign( BOX_W ),
      LayoutScaler.heightFromDesign( BOX_H ),
      ImgID.UI_GreenBox,
    );

    this.addChild( box );
  }

  private createInfoBlock(): void
  {
    const block_w = LayoutScaler.widthFromDesign( BLOCK_W );
    const block_h = LayoutScaler.heightFromDesign( BLOCK_H );

    const block = new RoundedRectangle(
      this.ctx,
      this.panelCenteredX( BLOCK_W ),
      this.panelY( BLOCK_Y ),
      block_w,
      block_h,
      LayoutScaler.cornerRadiusFromDesign( BLOCK_RADIUS ),
      ColorScheme.level_box_info_bg,
      undefined,
      false,
      undefined,
      0,
      {
        color: "rgba(255, 255, 255, 0.25)",
        offsetX: 0,
        offsetY: LayoutScaler.heightFromDesign( 3 ),
        blur: LayoutScaler.widthFromDesign( 6 )
      },
      {
        color: "rgba(0, 0, 0, 0.45)",
        offsetX: 0,
        offsetY: LayoutScaler.heightFromDesign( 5 ),
        blur: LayoutScaler.widthFromDesign( 10 )
      }
    );

    block.setClickable( false );
    this.addChild( block );

    const text_x = block_w * 0.05;
    const text_w = block_w * 0.9;

    const text = new UITextElement(
      this.ctx,
      text_x,
      0,
      text_w,
      block_h,
      {
        localization_key: "LEVEL_BOX.TEXT",
        text_variable_params: [ this.next_box_level.toString() ],
        color_map: { level: ColorScheme.level_box_info_accent },
        font_size: LayoutScaler.textSizeFromDesign( BLOCK_TEXT_SIZE ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 36 ),
        line_height: BLOCK_LINE_HEIGHT,
      }
    );

    text.setClickable( false );
    block.addChild( text );
  }

  private createButton(): void
  {
    const btn_w = LayoutScaler.widthFromDesign( BUTTON_W );
    const btn_h = LayoutScaler.heightFromDesign( BUTTON_H );

    const btn = new UIImage(
      this.ctx,
      this.panelCenteredX( BUTTON_W ),
      this.panelY( BUTTON_Y ),
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      {},
      true
    );

    const btn_text = new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "CONTINUE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 800,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.menu_button_outline,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    btn.setOnClick( () => DialogManager.closeDialog( "close_button" ) );

    btn.addChild( btn_text, true );
    this.addChild( btn );
  }

  private createCloseButton(): void
  {
    const btn = new UIImage(
      this.ctx,
      this.panelX( CLOSE_X ),
      this.panelY( CLOSE_Y ),
      LayoutScaler.widthFromDesign( CLOSE_SIZE ),
      LayoutScaler.heightFromDesign( CLOSE_SIZE ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );

    btn.setOnClick( () => DialogManager.closeDialog( "close_button" ) );

    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale = 0.7,
    overshoot_scale = 1.1,
    final_scale = 1.0,
    scale_up_duration = 136,
    settle_duration = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );
    super.playAppearAnimation(
      from_scale,
      overshoot_scale,
      final_scale,
      scale_up_duration,
      settle_duration
    );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public override containsPointGlobal( p: Vector2 ): boolean
  {
    const body_pos = this.pixelPosForRender();
    const body_scale = this.getScaleImportant();
    const body_center_x = body_pos.x + this.rect.getW() / 2;
    const body_center_y = body_pos.y + this.rect.getH() / 2;

    const hit_x = LayoutScaler.xFromDesign( HIT_X );
    const hit_y = LayoutScaler.yFromDesign( HIT_Y );
    const hit_w = LayoutScaler.widthFromDesign( HIT_W );
    const hit_h = LayoutScaler.heightFromDesign( HIT_H );

    const left = body_center_x + ( hit_x - this.rect.getW() / 2 ) * body_scale.x;
    const top = body_center_y + ( hit_y - this.rect.getH() / 2 ) * body_scale.y;
    const right = left + hit_w * body_scale.x;
    const bottom = top + hit_h * body_scale.y;

    return p.x >= left && p.x <= right && p.y >= top && p.y <= bottom;
  }

  public name(): string
  {
    return "LevelBoxDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
